// Importing React classes and functions from node modules
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { getProfileUsers, getGroups, getHomework, getResults, createResults, updateResults, getAllResults, getHomeworksByID, createHomeworks, editHomeworks, deleteHomeworksByID, createFinalResults, updateFinalResults, getFinalResultsByID } from "../data/repository";

// Functional Component for Login Page
function Homework(props) {
    // Declaration of useState Variables and Hook

    const [users, setUsersData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [groups, setGroupsData] = useState([]);
    const [homeworks, setHomeworks] = useState([]);
    const { className } = useParams();
    const { groupNumber } = useParams();
    const [results, setResults] = useState([]);
    const [genderFilter, setGenderFilter] = useState(props.user.gender === "Male" ? "Atfal" : props.user.gender === "Female" ? "Nasirat" : "All");

    useEffect(() => {

        // Loads User Details from DB
        async function loadUserDetails() {
            const currentDetails = await getProfileUsers();
            setUsersData(currentDetails)
            setIsLoading(false);
        }

        // Loads User Details from DB
        async function loadGroupDetails() {
            const currentGroups = await getGroups();
            setGroupsData(currentGroups)
        }

        // Loads Syllabus Details from DB
        async function loadHomeworkDetails() {
            const currentHomework = await getHomework();
            setHomeworks(currentHomework);
        }

        async function loadResultsDetails() {
            // Load the results for the current class and set the results state
            const currentResults = await getAllResults();
            setResults(currentResults);
        }

        // Calls the functions above
        loadUserDetails();
        loadGroupDetails();
        loadHomeworkDetails();
        loadResultsDetails();

    }, [className]);


    const handleSearch = async (event) => {
        setSearch(event.target.value.toLowerCase());
    }

    let groupDetails;

    const group = groups.find((group) => group.id === groupNumber);

    if (group) {
        groupDetails = group.group;
    } else {
        groupDetails = "Invalid group number";
    }


    const findNextUncheckedHomeworkID = (homeworksForClass, existingResult) => {
        for (let i = 0; i < homeworksForClass.length; i++) {
            const nextHomeworkID = homeworksForClass[i].id;
            const nextCheckboxID = `${nextHomeworkID}`;

            // Check if the next homework is unchecked for the student
            if (!existingResult || !(nextCheckboxID in existingResult.markedHomework) || !existingResult.markedHomework[nextCheckboxID]) {
                return nextHomeworkID;
            }
        }
        return null;
    };

    const findFatherName = (studentID) => {
        const userWithMatchingID = users.find(user => user.id === studentID);
        return userWithMatchingID ? userWithMatchingID.fathersName : "";
    };

    const findMotherName = (studentID) => {
        const userWithMatchingID = users.find(user => user.id === studentID);
        return userWithMatchingID ? userWithMatchingID.mothersName : "";
    };

    const getParentName = (studentID, gender) => {
        const fatherName = findFatherName(studentID);
        const motherName = findMotherName(studentID);
    
        if (gender === "Female" || gender === "Nasirat") {
            // Prefer mother's name, but fall back to father's name if mother's name is null/empty
            return motherName || fatherName || "";
        } else {
            // Prefer father's name, but fall back to mother's name if father's name is null/empty
            return fatherName || motherName || "";
        }
    };

    const handleCheckboxChange = async (studentID, homeworkID, checked, studentGroup) => {

        // Calculate the updated results locally first
        const updatedResults = results.map((result) => {
            if (result.studentID === studentID && result.studentGroup === studentGroup && result.class === className) {
                return {
                    ...result,
                    markedHomework: {
                        ...result.markedHomework,
                        [homeworkID]: checked,
                    },
                };
            }
            return result;
        });

        // Calculate the student result based on the updated results
        const studentResult = calculateStudentResults(studentID, studentGroup, updatedResults, true);

        const existingResult = await getResults(className, studentID);

        if (existingResult) {
            // Update the existing result in the database
            const updatedResult = {
                ...existingResult,
                markedHomework: {
                    ...existingResult.markedHomework,
                    [homeworkID]: checked,
                },
            };

            const homeworksForClass = homeworks.filter(homework => {
                if (groupDetails === "Invalid group number") {
                    return homework.classname === className && homework.group.includes(studentGroup);
                } else {
                    return homework.classname === className && homework.group.includes(groupDetails);
                }
            });

            const nextUncheckedHomeworkID = findNextUncheckedHomeworkID(homeworksForClass, updatedResult);


            if (nextUncheckedHomeworkID) {
                // Find the next homework text for the unchecked checkbox
                const nextHomeworkText = homeworks.find(homework => homework.id === nextUncheckedHomeworkID).homework;

                // Check if homework exists for the student and classname
                const existingHomework = await getHomeworksByID(className, studentID);


                if (existingHomework) {
                    // Homework already exists, edit the homework text in the database
                    await editHomeworks(className, studentID, { id: props.user.id, homeworkText: nextHomeworkText });
                } else {
                    // Homework doesn't exist, create new homework in the database
                    const homeworkData = {
                        class: className,
                        student: studentID,
                        id: props.user.id,
                        homeworkText: nextHomeworkText,
                    };
                    await createHomeworks(homeworkData);
                }
            } else {
                // If no unchecked homework is found, delete the existing homework item
                const existingHomework = await getHomeworksByID(className, studentID);
                if (existingHomework) {
                    await deleteHomeworksByID(className, studentID, existingHomework);
                }
            }

            const updatedRecord = await updateResults(className, updatedResult.markedHomework, studentID, studentGroup, studentResult);
            setResults([...results.filter((result) => result.studentID !== studentID), updatedRecord]);

            const existingFinalResults = await getFinalResultsByID(studentID);
            const student = users.find((user) => user.id === studentID);
            if (existingFinalResults)
                await updateFinalResults(studentID, null);
            else
                await createFinalResults(studentID, student?.name, student?.group, student?.gender, student?.fathersName, student?.mothersName, student?.fathersEmail, student?.studentEmail);

        } else {

            // Result to Calculate the Correct Initial Local Score for Student being marked for first time
            const newResult2 = [
                {
                    studentID,
                    studentGroup,
                    class: className,
                    markedHomework: {
                        [homeworkID]: checked,
                    },
                },
            ];

            // Create a new result in the database
            const newResult = {
                [homeworkID]: checked,
            };

            const studentResult = calculateStudentResults(studentID, studentGroup, newResult2, true);

            // Get the next homework item ID in the column
            // const homeworksForClass = homeworks.filter(homework => homework.classname === className && homework.group.includes(groupDetails));

            const homeworksForClass = homeworks.filter(homework => {
                // Check if groupDetails is "Invalid Number"
                if (groupDetails === "Invalid group number") {
                    // If groupDetails is "Invalid Number", use studentGroup
                    return homework.classname === className && homework.group.includes(studentGroup);
                } else {
                    // Otherwise, use groupDetails
                    return homework.classname === className && homework.group.includes(groupDetails);
                }
            });


            const currentIndex = homeworksForClass.findIndex(homework => homework.id === parseInt(homeworkID));
            const nextIndex = currentIndex + 1;
            const nextHomeworkID = nextIndex < homeworksForClass.length ? homeworksForClass[nextIndex].id : null;

            // Check if homework exists for the student and classname
            const existingHomework = await getHomeworksByID(className, studentID);

            if (existingHomework && nextHomeworkID) {
                // Homework already exists, edit the homework text in the database
                const nextHomeworkText = homeworks.find(homework => homework.id === nextHomeworkID).homework;
                await editHomeworks(className, studentID, { id: props.user.id, homeworkText: nextHomeworkText });
            } else if (!existingHomework && nextHomeworkID) {
                // Homework doesn't exist, create new homework in the database
                const nextHomeworkText = homeworks.find(homework => homework.id === nextHomeworkID).homework;
                const homeworkData = {
                    class: className,
                    student: studentID,
                    id: props.user.id,
                    homeworkText: nextHomeworkText,
                };

                await createHomeworks(homeworkData);
            }

            const createdResult = await createResults(className, newResult, studentID, studentGroup, studentResult);
            setResults([...results, createdResult]);

            const existingFinalResults = await getFinalResultsByID(studentID);
            const student = users.find((user) => user.id === studentID);

            if (existingFinalResults)
                await updateFinalResults(studentID, null);
            else
                await createFinalResults(studentID, student?.name, student?.group, student?.gender, student?.fathersName, student?.mothersName, student?.fathersEmail, student?.studentEmail);
        }
    };

    // Group Based Calculations Depending on what group student belongs they get marked accordingly
    const calculateStudentResults = (studentID, userGroup, updatedResult, flag) => {

        let filteredResult;

        if (flag) {
            filteredResult = updatedResult;
        } else {
            filteredResult = results;
        }

        const studentResults = filteredResult.filter(
            (result) =>
                result.studentID === studentID &&
                result.studentGroup === userGroup &&
                result.class === className
        );

        if (studentResults.length === 0) {
            return "0%";
        }

        const totalHomeworks = homeworks.filter(
            (homework) =>
                homework.classname === className &&
                homework.group.includes(userGroup)
        ).length;

        const checkedHomeworks = studentResults.reduce((count, result) => {
            return count + Object.entries(result.markedHomework).reduce((marks, [homeworkId, isChecked]) => {
                const homework = homeworks.find((item) => item.id.toString() === homeworkId);
                if (isChecked && homework && homework.group.includes(userGroup)) {
                    marks++;
                }
                return marks;
            }, 0);
        }, 0);

        let percentage = (checkedHomeworks / totalHomeworks) * 100;

        if (totalHomeworks === 0) {
            return "0%";
        }

        if (percentage > 100) {
            percentage = 100;
        }

        return percentage.toFixed(2) + "%";

    };

    const filteredHomeworksBasedOnClassName = homeworks.filter((homework) => homework.classname === className);

    const filteredHomeworks = filteredHomeworksBasedOnClassName.filter((homework) => {
        if (homework.group.includes(groupDetails)) {
            return true; // Include if groupDetails is found
        } else {
            return false; // Exclude if groupDetails is not found
        }
    });

    const filteredArray = groupDetails === "Invalid group number" ?
        filteredHomeworksBasedOnClassName.filter((homework) => homework.classname === className) :
        filteredHomeworks;

    return (

        <div>
            <p>&nbsp;</p>
            <h3 className="text-center">Mark Homework {className}:</h3>

            <br />
            <div className="searchCenter">
                <div className="form-group has-search">
                    <span className="fa fa-search form-control-feedback"></span>
                    <input type="text" style={{ border: "1px solid #112c3f", borderRadius: "10rem" }} className="form-control" placeholder="Search" aria-label="Search" onChange={handleSearch} />
                </div>
            </div>

            {(props.user.group === "Admin" || (props.user.group === "Principal" && props.user.gender === "Male")) && (
                <div style={{ textAlign: "center", margin: "0.5rem" }}>
                    <select style={{ border: "1px solid #112c3f", borderRadius: "0.5rem", padding: "0.25rem 0.5rem" }} value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                        <option value="All">All</option>
                        <option value="Atfal">Atfal</option>
                        <option value="Nasirat">Nasirat</option>
                    </select>
                    <div style={{ fontSize: "0.75rem", color: "#555", marginTop: "0.25rem" }}>
                        Gender
                    </div>
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "center" }}>
                <Link to="/SelectGroupMarkHomework" state={{ homeworkClasses: className }}>
                    <button type="button" style={{ margin: "5px" }} className="text-center btn btn-success">Go Back to Select Group For {className}</button>
                </Link>
            </div>

            <div className="text-center">
                <p style={{ color: "#de0300" }}>Please note only the homework after the first ticked checkbox will be displayed as the homework for that Student on their dashboard</p>
            </div>

            <div className="table-responsive">

                {isLoading ?
                    <div className="card-body text-center">
                        <span className="text-muted">Loading student...</span>
                    </div>
                    :
                    <div className="table-container1">
                        <table className="table table-striped" style={{ margin: "0" }}>
                            <thead>
                                <tr>
                                    {/* <th></th> */}
                                    <th className="sticky-col" style={{ color: "#112c3f" }} scope="col">ID</th>
                                    <th className="sticky-col" style={{ color: "#112c3f" }} scope="col">Name</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Parent</th>
                                    {/* Render homework columns */}
                                    {groupDetails === "Invalid group number" ? (
                                        // Render filteredHomeworksBasedOnHomework if groupDetails is "Invalid group number"
                                        filteredHomeworksBasedOnClassName.map((homework) => (
                                            <th key={homework.id}>{homework.homework}</th>
                                        ))
                                    ) : (
                                        // Render filteredHomeworks if groupDetails is not "Invalid group number"
                                        filteredHomeworks.map((homework) => (
                                            <th key={homework.id}>{homework.homework}</th>
                                        ))
                                    )}
                                    {homeworks.some((homework) => homework.classname === className) && (
                                        <th style={{ color: "#112c3f" }} scope="col">Results</th>
                                    )}

                                </tr>
                            </thead>
                            {/* Mapping Users state Variable to access its content easily to display in Table */}
                            {users.sort((a, b) => {
                                if (a.name && b.name) { return a.name.localeCompare(b.name); } return 0; /* Keep order for items without names */
                            }).filter((userDetails) => {
                                return search.toLowerCase() === '' ? userDetails : (userDetails.name && userDetails.name.toLowerCase().includes(search)) || (userDetails.group && userDetails.group.toLowerCase().includes(search)) || (userDetails.id && userDetails.id.toLowerCase().includes(search)) || (userDetails.gender && userDetails.gender.toLowerCase().includes(search));
                            }).map((userDetails) => {

                                return (
                                    <tbody key={userDetails.id}>
                                        {(userDetails.name !== props.user.name && userDetails.group !== "Admin" && userDetails.group !== "Male Teacher" && userDetails.group !== "Female Teacher" && userDetails.group !== "Principal" && userDetails.group !== "Vice Principal" && userDetails.group !== "Admin Staff") &&
                                            <>
                                                {/* If logged in user is FemaleTeachers then Display only Nasirat List and If MaleTeahers are logged in show only Atfal list or if Admin is logged in show full list*/}
                                                {((props.user.group === "Female Teacher" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Male Teacher" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Principal" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    // (props.user.group === "Principal" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Principal" && props.user.gender === "Male" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Vice Principal" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Vice Principal" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin Staff" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin Staff" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails))
                                                ) &&
                                                    ((props.user.group === "Admin" || (props.user.group === "Principal" && props.user.gender === "Male"))
                                                    ? (genderFilter === "All" || 
                                                        (genderFilter === "Atfal" && (userDetails.gender === "Male" || userDetails.gender === "Atfal")) || 
                                                        (genderFilter === "Nasirat" && (userDetails.gender === "Female" || userDetails.gender === "Nasirat"))
                                                    )
                                                    : true) &&
                                                    <>
                                                        <tr>
                                                            {/* <td></td> */}
                                                            <td className="sticky-col" style={{ color: "#112c3f" }}>{userDetails.id}</td>
                                                            <td className="sticky-col" style={{ color: "#112c3f" }}>{userDetails.name}</td>
                                                            <td style={{ color: "#112c3f" }}>
                                                                {getParentName(userDetails.id, userDetails.gender)}
                                                            </td>


                                                            {filteredArray.map((homework) => {
                                                                const checkboxID = `${homework.id}`;
                                                                const isChecked = results.some((result) =>
                                                                    result.studentID === userDetails.id &&
                                                                    result.markedHomework[checkboxID] === true &&
                                                                    result.class === className
                                                                );

                                                                return (
                                                                    <td key={homework.id}>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="checkbox"
                                                                            checked={isChecked}
                                                                            onChange={(e) => handleCheckboxChange(userDetails.id, checkboxID, e.target.checked, userDetails.group)}
                                                                        />
                                                                    </td>
                                                                );
                                                            })}
                                                            {homeworks.some((homework) => homework.classname === className) && (
                                                                <td style={{ color: "#112c3f" }}>
                                                                    {calculateStudentResults(userDetails.id, userDetails.group)}
                                                                </td>
                                                            )}

                                                        </tr>
                                                    </>
                                                }
                                            </>
                                        }
                                    </tbody>
                                )
                            })}
                        </table>
                    </div>
                }
            </div>
        </div>
    );
}

// Export the Homework Function
export default Homework;
